import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const UruguayPage = () => {
  const uruguayData = {
    name: 'Uruguay',
    region: 'Latin America',
    color: '#C85A54',
    metaTitle: 'Pharma & Nutraceutical Registration in Uruguay | MSP | CTD Ready',
    metaDescription: 'Register pharmaceutical and nutraceutical products in Uruguay. MSP regulatory framework, CTD dossier adoption (2024), import/export logistics via free zones, key requirements and opportunities.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Uruguay',
      subtitle: 'A stable South American market with high regulatory standards, recent CTD alignment and strategic Mercosur/logistics advantages—ideal for quality exporters targeting the Southern Cone.',
      cta: 'Start Your Uruguay Regulatory Entry'
    },
    
    atAGlance: [
      { label: 'Population', value: '~3.5 million (2024 estimate)' },
      { label: 'Income Level', value: 'High-income classified by World Bank' },
      { label: 'Regulator', value: 'Ministry of Public Health (MSP) – Directorate of Medicines & Health Products' },
      { label: 'Recent Reform', value: 'CTD‐format adoption effective October 2024' },
      { label: 'Logistics Hub', value: 'Positioned for import/distribution via Montevideo, free port/zone infrastructure' },
      { label: 'Strategic Value', value: 'Mercosur membership, regional distribution base' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Strategic, Quality-Driven and Hub-oriented',
      stats: [
        'Well-established healthcare sector, though new product approvals can exceed 12 months',
        'Uruguay adopted full CTD structuring for medicines registration as of October 2024',
        'CTD adoption enhances harmonisation and enables dossier reuse from global submissions',
        'Free trade zones provide duty/distribution advantages for regional operations',
        'Mercosur membership makes Uruguay well-placed for regional distribution into South America',
        'Smaller market size but high regulatory maturity and predictable entry paths'
      ],
      opportunities: [
        'High-quality dossiers essential - CTD compatibility enables leverage of existing data assets',
        'Consider Uruguay as regional entry hub rather than solely national-market play',
        'Higher regulatory expectations correspond with higher market-entry value for premium solutions',
        'Strategic positioning for Southern Cone distribution and Mercosur access'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: NCDs, Aging Population & Premium Opportunities',
      subtitle: 'Uruguay has an aging population and increasing non-communicable disease burden as part of South-American health trends.',
      categories: [
        {
          name: 'Non-Communicable Diseases & Aging',
          items: [
            'Aging population driving chronic disease management needs',
            'Increasing cardiovascular disease burden',
            'Rising diabetes and obesity prevalence in line with regional trends',
            'High-income profile supports demand for quality therapies',
            'Healthcare infrastructure capable of supporting advanced treatments',
            'Pharma demand: Antidiabetics, lipid-lowering agents, antihypertensives',
            'Pharma demand: Biologics and specialty medicines with regulatory alignment'
          ]
        },
        {
          name: 'Premium Wellness & Preventive Health',
          items: [
            'Higher-end therapies and generics with premium positioning',
            'Wellness-oriented supplements aligned with aging population needs',
            'Cardiovascular support and metabolic health products in demand',
            'Seniors-health formulations (joint, bone, cognitive support)',
            'Nutra: Premium supplements - omega-3s, CoQ10, joint/arthritis support',
            'Nutra: Women\'s health formulations for quality-conscious consumers',
            'Wellness and preventive care segments less saturated than larger LatAm markets'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Ministry of Public Health (MSP) - Ministerio de Salud Pública',
      description: 'MSP oversees regulation of medicines, health-products, biologics and nutritional/health supplements. Uruguay adopted the CTD format for medicine registration effective October 2024, implementing Decree 018/020 (communicated April 2024). Legal framework includes Law 15,443 (Drugs Law), Decree 521/984, Decree 12/007 for pharmaceuticals. Nutritional products regulated by MSP requiring registration/import authorisation.',
      pharma: {
        timeline: 'While CTD adoption improves predictability, some registrations in Uruguay can take beyond 12 months. Planning for 12–18 months is prudent. CTD format adoption from October 2024 should gradually improve timeline consistency as system matures.',
        requirements: [
          'Legal local entity (importer/distributor) required; registration via MSP initiated locally',
          'Full CTD format adoption from October 2024 - dossiers must align with Modules 1-5 structure',
          'Administrative forms and local representative documentation',
          'Quality dossier (Module 3): manufacturing process, specifications, stability data, GMP certificates',
          'Non-clinical/clinical data (Modules 4/5) as applicable for innovator/biologic products',
          'Spanish labelling, packaging design, patient information leaflets',
          'Local legal compliance and regulatory requirements',
          'Free Sale Certificate / CPP from reference authority',
          'Strong quality/CMC content with local adaptation required',
          'Local stability data under appropriate climatic conditions',
          'Standard lifecycle processes apply (renewals, modifications, post-approval changes)',
          'Import/distribution authorisation via local entity'
        ],
        insight: 'Uruguay\'s October 2024 CTD adoption represents major regulatory modernisation. This alignment with international standards means exporters with existing CTD dossiers can more efficiently enter the market. The high regulatory maturity ensures quality products are valued, making Uruguay ideal for premium portfolios targeting the Southern Cone.'
      },
      nutra: {
        timeline: 'Classification-dependent. Products correctly classified as nutritional products or dietary supplements may have different pathways than pharmaceutical medicines. Timeline depends on classification, MSP workload and documentation quality. Planning for several months to over a year recommended.',
        requirements: [
          'Nutritional products, dietary supplements and health-support items regulated by MSP',
          'Sanitary authorisation or registration required to market products',
          'Correct classification critical: food vs supplement vs medicine',
          'Claims, dosage form and composition drive pathway and documentation burden',
          'Formulation details and evidence of safety required',
          'Manufacturing control documentation (GMP or food GMP certificates)',
          'Spanish labelling with permissible claims, warnings and user instructions',
          'Local importer registration mandatory',
          'Product positioning must align with local regulation on health claims',
          'Premium claims (e.g., "supports cardiovascular health") must avoid disease-treatment territory',
          'Sanitary authorisation for marketing may be required',
          'Free zone infrastructure can support distribution logistics'
        ],
        insight: 'Uruguay regulates nutritional products and supplements through MSP with emphasis on proper classification. Products must clearly distinguish between food supplementation and medicinal claims. The relatively sophisticated market appreciates premium wellness products, but documentation and local compliance are non-negotiable. Uruguay\'s smaller size means fewer competitors in certain wellness segments, creating differentiation opportunities.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with MSP (CTD-Aligned)',
      steps: [
        'Local Agent / MAH - Establish legal local entity (importer/distributor) for registration via MSP',
        'CTD Dossier Format - Full adoption from October 2024; align with Modules 1-5 structure',
        'Administrative Documentation - Forms and local representative documentation',
        'Module 1 - Administrative & local requirements specific to Uruguay',
        'Module 2 - Quality Overall Summary, non-clinical and clinical overviews',
        'Module 3 - Quality/CMC - Manufacturing process, specifications, stability data, GMP certificates',
        'Module 4 - Non-Clinical Data (as applicable for innovator products)',
        'Module 5 - Clinical Data (as applicable for innovator/biologic products)',
        'GMP Certificate - From manufacturing site, recognised by Uruguay authorities',
        'Free Sale Certificate / CPP - From reference authority to support application',
        'Spanish Labelling - Complete labelling, packaging design, patient information leaflets',
        'Local Legal Compliance - Ensure all regulatory requirements met',
        'Local Stability Data - Under appropriate climatic conditions for Uruguay',
        'Import/Distribution Authorisation - Via local entity with MSP approval',
        'MSP Assessment - Technical evaluation; expect 12+ months timeline',
        'Registration Approval - Certificate issued for defined validity period',
        'Lifecycle Management - Renewals, modifications, post-approval changes per MSP procedures'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals & Wellness Products in Uruguay',
      steps: [
        'Classification Analysis - Determine food vs supplement vs medicine category',
        'MSP Regulation - Nutritional products require sanitary authorisation/registration',
        'Pathway Selection - Claims, dosage form and composition drive requirements',
        'Formulation Documentation - Complete product details and ingredient disclosure',
        'Safety Evidence - Documentation supporting safe use of all ingredients',
        'Manufacturing Control - GMP or food GMP certificates where applicable',
        'Quality Documentation - Manufacturing standards and quality assurance',
        'Spanish Labelling - Complete label artwork with permissible claims',
        'Claims Compliance - Align with local regulation; avoid disease-treatment claims',
        'Premium Positioning - "Supports cardiovascular health" vs disease claims',
        'User Instructions - Clear dosage, warnings and usage guidance',
        'Local Importer Registration - Establish authorised local entity',
        'Sanitary Authorisation - Obtain MSP marketing approval',
        'Free Zone Logistics - Leverage Uruguay infrastructure for distribution',
        'Regional Strategy - Position for Mercosur distribution opportunities'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals': [
        'Local representative appointment documentation',
        'GMP certificate for manufacturing site',
        'CPP / Free Sale Certificate from reference authority',
        'Complete CTD dossier (Modules 1-5) adapted for Uruguay',
        'Module 3 Quality dossier with stability data',
        'Spanish labelling and packaging design',
        'Patient information leaflets in Spanish',
        'Import/distribution authorisation via local entity',
        'Local legal compliance documentation',
        'Local stability data under Uruguay conditions'
      ],
      'Nutraceuticals / Wellness Products': [
        'Product formulation and dosage details',
        'Manufacturing and quality certificates (GMP or food GMP)',
        'Safety and usage evidence for all ingredients',
        'Ingredient lists acceptable in Uruguay regulatory framework',
        'Spanish label artwork with permissible claims',
        'Warnings and user instructions',
        'Local importer/distributor documentation',
        'Sanitary authorisation application for marketing',
        'Classification justification (food vs supplement vs medicine)',
        'Evidence supporting health claims where applicable'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Uruguay Should Be in Your Region Strategy',
      reasons: [
        'High Regulatory Standard & CTD Alignment - Full CTD adoption (October 2024) enables reuse of global dossier assets',
        'Logistics & Mercosur Access - Distribution hub for Southern Cone and Latin America with free-zone advantages',
        'Premium Market Positioning - Smaller market but high-income and sophisticated healthcare system values quality',
        'Strategic Gateway - Well-positioned for regional distribution into Argentina, Brazil, Chile',
        'Wellness Market Opportunity - Rising wellness demand with fewer players than large markets enables differentiation',
        'Regulatory Maturity - Predictable pathways with high standards reward quality portfolios',
        'Free Zone Infrastructure - Montevideo port and free zones support efficient regional logistics'
      ],
      bestPortfolio: [
        'Pharma: Chronic-disease medicines (antidiabetics, cardiovascular, lipid management); high-quality generics with CTD structure; biologics and specialty medicines leveraging regulatory alignment',
        'Nutra/Wellness: Premium supplements for cardiovascular support, metabolic health, women\'s health, joint/elderly care (omega-3, CoQ10, joint support, bone health); products positioned for quality-conscious, aging population'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Uruguay',
      services: [
        'Portfolio Fit & Market Mapping - Assess which SKUs match Uruguay NCD profile, health system and regulatory expectations',
        'CTD Dossier Adaptation - Convert existing global dossier into Uruguay-compliant CTD format with Spanish localisation',
        'Submission Strategy - Handle GMP/CPP verification, Module 3 quality optimization, MSP submission procedures',
        'Wellness & Supplement Classification - Advise on classification of nutraceuticals with necessary documentation and labelling strategy',
        'Spanish Localisation - Complete translation and adaptation of labels, leaflets and regulatory documentation',
        'Local Partner/Distribution Setup - Support selecting local entity, import/distribution planning, free-zone logistics',
        'Mercosur Regional Planning - Use Uruguay registration assets as platform for expansion into Argentina, Brazil, Chile',
        'Lifecycle Management - Plan variants, renewals and post-approval changes per MSP requirements'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines and health products in Uruguay?',
        answer: 'The Ministry of Public Health (MSP - Ministerio de Salud Pública) is the central regulatory authority for medicines, health-products, biologics, nutritional products and their authorisation in Uruguay.'
      },
      {
        question: 'Does Uruguay require CTD-format dossiers?',
        answer: 'Yes. Uruguay formally adopted CTD format (Modules 1-5) for medicine registration effective October 2024. This alignment with international standards makes Uruguay more accessible for exporters with existing CTD dossiers.'
      },
      {
        question: 'How long does the registration process take?',
        answer: 'Timelines vary, and while CTD alignment should improve predictability, some reports indicate more than 12 months in many cases. Planning for 12–18 months is prudent, especially during the transition period following CTD adoption.'
      },
      {
        question: 'How are nutraceuticals regulated?',
        answer: 'Nutritional products and supplements fall under MSP regulation and require sanitary authorisation or registration depending on classification (food vs supplement vs medicinal). Proper classification based on claims, dosage form and composition is critical.'
      },
      {
        question: 'Can Uruguay serve as a regional hub for distribution?',
        answer: 'Yes. Uruguay\'s logistics infrastructure, free-zone advantages and Mercosur membership make it a strategically valuable distribution base for Latin America, particularly for the Southern Cone region including Argentina, Brazil and Chile.'
      }
    ]
  };

  return <CountryTemplate countryData={uruguayData} />;
};
